import { useMemo } from 'react';
import type React from 'react';
import { useAppSelector } from '../store';
import { UserRole, User } from '../types';

export enum Permission {
  DASHBOARD = 'dashboard',
  VISION_DATA = 'vision_data',
  HEALTH_DATA = 'health_data',
  VISION_ENTRY = 'vision_entry',
  HEALTH_ENTRY = 'health_entry',
  VACCINE_PLAN = 'vaccine_plan',
  VACCINE_CONFIG = 'vaccine_config',
  DATA_EXPORT = 'data_export',
  DEVICE_MANAGEMENT = 'device_management',
  USER_MANAGEMENT = 'user_management',
  ROLE_MANAGEMENT = 'role_management',
  MENU_MANAGEMENT = 'menu_management',
  MEMBER_MANAGEMENT = 'member_management',
  COMMUNITY_MANAGEMENT = 'community_management',
  SCHOOL_MANAGEMENT = 'school_management',
  CLASS_MANAGEMENT = 'class_management',
  STUDENT_MANAGEMENT = 'student_management',
  REGULATION_MANAGEMENT = 'regulation_management',
  SYSTEM_SETTINGS = 'system_settings',
  WORKFLOW_MANAGEMENT = 'workflow_management',
  ALL = 'all',
}

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.DISEASE_CONTROL]: [Permission.ALL],
  [UserRole.SCHOOL_MEDICAL]: [
    Permission.DASHBOARD,
    Permission.VISION_DATA,
    Permission.VISION_ENTRY,
    Permission.VACCINE_PLAN,
    Permission.DATA_EXPORT,
  ],
  [UserRole.COMMUNITY]: [
    Permission.DASHBOARD,
    Permission.HEALTH_DATA,
    Permission.HEALTH_ENTRY,
    Permission.VACCINE_PLAN,
    Permission.DATA_EXPORT,
  ],
  [UserRole.DOCTOR]: [
    Permission.DASHBOARD,
    Permission.VACCINE_PLAN,
    Permission.DATA_EXPORT,
  ],
};

export const usePermissions = () => {
  const { user } = useAppSelector(state => state.auth);

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;

    const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
    if (rolePermissions.includes(Permission.ALL)) return true;

    if (user.permissions && user.permissions.length > 0) {
      return user.permissions.some(p => p === permission || p === Permission.ALL);
    }

    return rolePermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user) return false;

    const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
    if (rolePermissions.includes(Permission.ALL)) return true;

    if (user.permissions && user.permissions.length > 0) {
      return user.permissions.includes(Permission.ALL) || permissions.some(permission => user.permissions?.some(p => p === permission));
    }

    return permissions.some(permission => rolePermissions.includes(permission));
  };

  const getAccessiblePaths = useMemo(() => {
    if (!user) return [];

    const paths: string[] = ['/dashboard'];

    switch (user.role) {
      case UserRole.DISEASE_CONTROL:
        paths.push(
          '/data-query/vision-screening',
          '/data-query/health-monitoring',
          '/data-entry',
          '/data-export',
          '/vaccine-plan',
          '/device-management',
          '/user-management',
          '/mini-program/user-config',
          '/mini-program/staff-config',
          '/system-settings',
        );
        break;
      case UserRole.SCHOOL_MEDICAL:
        paths.push('/data-query/vision-screening', '/data-entry', '/vaccine-plan', '/data-export');
        break;
      case UserRole.COMMUNITY:
        paths.push('/data-query/health-monitoring', '/data-entry', '/vaccine-plan', '/data-export');
        break;
      case UserRole.DOCTOR:
        paths.push('/vaccine-plan', '/data-export');
        break;
    }

    return paths;
  }, [user]);

  const filterDataByPermission = <T>(data: T[], filterFn: (item: T, user: User) => boolean): T[] => {
    if (!user || user.role === UserRole.DISEASE_CONTROL) {
      return data;
    }

    return data.filter(item => filterFn(item, user));
  };

  const canAccessVisionData = hasPermission(Permission.VISION_DATA);
  const canAccessHealthData = hasPermission(Permission.HEALTH_DATA);
  const canManageVaccinePlan = hasPermission(Permission.VACCINE_PLAN);
  const canEnterVisionData = hasPermission(Permission.VISION_ENTRY);
  const canEnterHealthData = hasPermission(Permission.HEALTH_ENTRY);
  const canManageDevices = hasPermission(Permission.DEVICE_MANAGEMENT);
  const canManageUsers = hasPermission(Permission.USER_MANAGEMENT);
  const canManageSystem = hasPermission(Permission.SYSTEM_SETTINGS);

  return {
    user,
    hasPermission,
    hasAnyPermission,
    getAccessiblePaths,
    filterDataByPermission,
    canAccessVisionData,
    canAccessHealthData,
    canManageVaccinePlan,
    canEnterVisionData,
    canEnterHealthData,
    canManageDevices,
    canManageUsers,
    canManageSystem,
  };
};

export interface MenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  permission?: Permission;
}

export const getVisibleMenuItems = (userRole: UserRole): MenuItem[] => {
  const baseMenu: MenuItem[] = [
    {
      key: 'dashboard',
      label: '数据概览',
      icon: 'DashboardOutlined',
      path: '/dashboard',
    },
  ];

  const roleSpecificMenus: Record<UserRole, MenuItem[]> = {
    [UserRole.DISEASE_CONTROL]: [
      {
        key: 'user-management',
        label: '用户管理',
        icon: 'UserOutlined',
        path: '/user-management',
      },
      {
        key: 'system-settings',
        label: '系统设置',
        icon: 'SettingOutlined',
        path: '/system-settings',
      },
    ],
    [UserRole.SCHOOL_MEDICAL]: [],
    [UserRole.COMMUNITY]: [],
    [UserRole.DOCTOR]: [],
  };

  return [...baseMenu, ...(roleSpecificMenus[userRole] || [])];
};
